/**
 * Dynamic config form for a writing skill.
 *
 * Renders a form field for each entry in `manifest.config_schema`.
 * Field types: string → text input, integer → number input, select → <select>.
 */
import type { SkillConfigField, SkillManifest } from './types'

interface Props {
  manifest: SkillManifest
  config: Record<string, unknown>
  onChange: (config: Record<string, unknown>) => void
}

export default function SkillConfigForm({ manifest, config, onChange }: Props) {
  if (manifest.config_schema.length === 0) {
    return (
      <div className="text-sm text-muted-foreground italic">
        No configuration options for this skill.
      </div>
    )
  }

  const set = (key: string, value: unknown) => {
    onChange({ ...config, [key]: value })
  }

  return (
    <div className="space-y-3">
      {manifest.config_schema.map((field) => (
        <ConfigField
          key={field.key}
          field={field}
          value={config[field.key]}
          onChange={(v) => set(field.key, v)}
        />
      ))}
    </div>
  )
}

function ConfigField({
  field,
  value,
  onChange,
}: {
  field: SkillConfigField
  value: unknown
  onChange: (v: unknown) => void
}) {
  const inputClass =
    'mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-foreground text-sm'

  const displayValue = value !== undefined && value !== null ? value : (field.default ?? '')

  return (
    <label className="block">
      <div className="flex items-baseline gap-1.5">
        <span className="text-sm font-medium">{field.label}</span>
        {field.required && <span className="text-xs text-destructive">required</span>}
      </div>
      {field.description && (
        <div className="text-xs text-muted-foreground mt-0.5">{field.description}</div>
      )}

      {field.type === 'select' ? (
        <select
          className={inputClass}
          value={String(displayValue)}
          onChange={(e) => onChange(e.target.value)}
        >
          {field.options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      ) : field.type === 'integer' ? (
        <input
          type="number"
          className={inputClass}
          value={displayValue === null || displayValue === '' ? '' : String(displayValue)}
          placeholder={field.default !== null && field.default !== undefined ? String(field.default) : undefined}
          onChange={(e) => {
            const raw = e.target.value
            onChange(raw === '' ? null : parseInt(raw, 10))
          }}
        />
      ) : (
        <input
          type="text"
          className={inputClass}
          value={String(displayValue)}
          placeholder={field.required ? 'Required' : undefined}
          onChange={(e) => onChange(e.target.value || null)}
        />
      )}
    </label>
  )
}
